import { useUser } from "@4uhub/lib4uhub";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import {
  ReportPeriodicity,
  type IReportColumn,
  type IReportResult,
} from "../models/report.dto";
import { getInternalReportById } from "../pages/4uSpots/Reports/internalReports";
import { getReportCode } from "../pages/4uSpots/Reports/shared";
import { getUserName } from "../services/user.service";
import {
  createReportTableRows,
  executeReport,
  isSupportedReportCode,
  normalizeReportResult,
  normalizeReportPeriodicity,
  parseReportResultSearchParams,
  REPORT_EXECUTION_TRANSLATION_PATH,
  shouldShowAutomationCodeExceptForReport,
  shouldShowAutomationCodeForReport,
  shouldShowEsignRulesForReport,
  shouldShowEstablishmentsForReport,
  shouldShowTicketChannelsForReport,
  shouldShowTicketServicesForReport,
  shouldShowUsersForReport,
  shouldShowWorkspacesForReport,
  type IReportFilterSummaryItem,
  type IReportTableRow,
} from "../pages/4uSpots/Reports/ReportExecution/reportExecutionUtils";

const formatDate = (value: string | undefined, language: string) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(language).format(date);
};

const formatPeriod = (
  startDate: string | undefined,
  endDate: string | undefined,
  language: string,
  allLabel: string,
) => {
  if (!startDate && !endDate) return allLabel;

  return `${formatDate(startDate, language)} - ${formatDate(
    endDate,
    language,
  )}`;
};

const formatMonth = (value: string | undefined, language: string) => {
  if (!value) return undefined;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(language, {
    month: "long",
  }).format(date);
};

const formatList = (values: string[] | undefined, allLabel: string) => {
  if (!values?.length) return allLabel;

  return values.join(", ");
};

const formatDateTime = (value: Date, language: string) =>
  new Intl.DateTimeFormat(language, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(value);

export const useReportResult = () => {
  const { i18n, t } = useTranslation();
  const { user } = useUser();
  const { reportId } = useParams<{ reportId: string }>();
  const [searchParams] = useSearchParams();
  const generationDate = useMemo(() => new Date(), []);

  const parsedParams = useMemo(
    () => parseReportResultSearchParams(searchParams),
    [searchParams],
  );
  const internalReport = useMemo(
    () => getInternalReportById(reportId),
    [reportId],
  );

  const {
    reportCode,
    reportName,
    startDate,
    endDate,
    ticketServiceIds,
    ticketChannelIds,
    userIds,
    workspaceId,
    ticketWorkspaceIds,
    workspaceNames,
    ticketServiceNames,
    ticketChannelNames,
    userNames,
    automationCode,
    automationCodeExcept,
    establishmentId,
    esignRuleId,
    automationCodeNames,
    automationCodeExceptNames,
    establishmentNames,
    esignRuleNames,
  } = parsedParams;

  const resolvedReportCode = useMemo(() => {
    if (reportCode) return reportCode;

    const internalReportCode = getReportCode(internalReport);
    const normalizedCode =
      typeof internalReportCode === "string"
        ? Number(internalReportCode)
        : internalReportCode;

    return normalizedCode !== undefined &&
      normalizedCode !== null &&
      isSupportedReportCode(normalizedCode)
      ? normalizedCode
      : undefined;
  }, [internalReport, reportCode]);

  const [isLoading, setIsLoading] = useState(false);
  const [reportResult, setReportResult] = useState<IReportResult>(() =>
    normalizeReportResult(),
  );
  const [rows, setRows] = useState<IReportTableRow[]>([]);
  const [columns, setColumns] = useState<IReportColumn[]>([]);
  const [periodicity, setPeriodicity] = useState<ReportPeriodicity>(
    ReportPeriodicity.Default,
  );

  const pageTitle = useMemo(
    () =>
      reportName ??
      internalReport?.name ??
      (t(REPORT_EXECUTION_TRANSLATION_PATH + "title") as string),
    [internalReport?.name, reportName, t],
  );

  const shouldShowTicketServices = useMemo(
    () => shouldShowTicketServicesForReport(resolvedReportCode),
    [resolvedReportCode],
  );

  const shouldShowTicketChannels = useMemo(
    () => shouldShowTicketChannelsForReport(resolvedReportCode),
    [resolvedReportCode],
  );

  const shouldShowWorkspaces = useMemo(
    () => shouldShowWorkspacesForReport(resolvedReportCode),
    [resolvedReportCode],
  );

  const shouldShowUsers = useMemo(
    () => shouldShowUsersForReport(resolvedReportCode),
    [resolvedReportCode],
  );

  const shouldShowAutomationCode = useMemo(
    () => shouldShowAutomationCodeForReport(resolvedReportCode),
    [resolvedReportCode],
  );

  const shouldShowAutomationCodeExcept = useMemo(
    () => shouldShowAutomationCodeExceptForReport(resolvedReportCode),
    [resolvedReportCode],
  );

  const shouldShowEstablishments = useMemo(
    () => shouldShowEstablishmentsForReport(resolvedReportCode),
    [resolvedReportCode],
  );

  const shouldShowEsignRules = useMemo(
    () => shouldShowEsignRulesForReport(resolvedReportCode),
    [resolvedReportCode],
  );

  const filterItems = useMemo<IReportFilterSummaryItem[]>(() => {
    const allLabel = t(REPORT_EXECUTION_TRANSLATION_PATH + "all") as string;
    const buildListItem = (
      labelKey: string,
      values: string[] | undefined,
    ): IReportFilterSummaryItem => ({
      label: t(REPORT_EXECUTION_TRANSLATION_PATH + labelKey),
      value: formatList(values, allLabel),
      values: values?.length ? values : undefined,
    });
    const items: IReportFilterSummaryItem[] = [
      {
        label: t(REPORT_EXECUTION_TRANSLATION_PATH + "metadata.period"),
        value: formatPeriod(startDate, endDate, i18n.language, allLabel),
      },
    ];

    if (shouldShowTicketServices) {
      items.push(
        buildListItem(
          "ticketServices",
          ticketServiceNames?.length ? ticketServiceNames : ticketServiceIds,
        ),
      );
    }

    if (shouldShowTicketChannels) {
      items.push(
        buildListItem(
          "channels",
          ticketChannelNames?.length ? ticketChannelNames : ticketChannelIds,
        ),
      );
    }

    if (shouldShowWorkspaces) {
      items.push(
        buildListItem(
          "workspaces",
          workspaceNames?.length ? workspaceNames : workspaceId,
        ),
      );
    }

    if (shouldShowUsers) {
      items.push(
        buildListItem("attendants", userNames?.length ? userNames : userIds),
      );
    }

    if (shouldShowAutomationCode) {
      items.push(
        buildListItem(
          "automationCode",
          automationCodeNames?.length ? automationCodeNames : automationCode,
        ),
      );
    }

    if (shouldShowAutomationCodeExcept) {
      items.push(
        buildListItem(
          "automationCodeExcept",
          automationCodeExceptNames?.length
            ? automationCodeExceptNames
            : automationCodeExcept,
        ),
      );
    }

    if (shouldShowEstablishments) {
      items.push(
        buildListItem(
          "establishments",
          establishmentNames?.length ? establishmentNames : establishmentId,
        ),
      );
    }

    if (shouldShowEsignRules) {
      items.push(
        buildListItem(
          "esignRules",
          esignRuleNames?.length ? esignRuleNames : esignRuleId,
        ),
      );
    }

    return items;
  }, [
    endDate,
    automationCode,
    automationCodeExcept,
    automationCodeExceptNames,
    automationCodeNames,
    establishmentId,
    establishmentNames,
    esignRuleId,
    esignRuleNames,
    i18n.language,
    shouldShowAutomationCode,
    shouldShowAutomationCodeExcept,
    shouldShowEsignRules,
    shouldShowEstablishments,
    shouldShowTicketChannels,
    shouldShowWorkspaces,
    shouldShowTicketServices,
    shouldShowUsers,
    startDate,
    t,
    ticketChannelIds,
    ticketChannelNames,
    userIds,
    userNames,
    workspaceId,
    workspaceNames,
    ticketServiceIds,
    ticketServiceNames,
  ]);

  const selectedMonth = useMemo(() => {
    if (!startDate || !endDate) return null;

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return null;
    }

    const lastDayOfMonth = new Date(
      end.getFullYear(),
      end.getMonth() + 1,
      0,
    ).getDate();
    const isSingleFullMonth =
      start.getDate() === 1 &&
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      end.getDate() === lastDayOfMonth;

    if (!isSingleFullMonth) return null;

    return formatMonth(startDate, i18n.language) ?? null;
  }, [endDate, i18n.language, startDate]);

  const generatedAt = useMemo(
    () => formatDateTime(generationDate, i18n.language),
    [generationDate, i18n.language],
  );

  const generatedBy = useMemo(
    () => user?.fullName?.trim() || getUserName()?.trim() || "-",
    [user],
  );

  const fetchReport = useCallback(async () => {
    if (!resolvedReportCode) {
      setReportResult(normalizeReportResult());
      setRows([]);
      setColumns([]);
      setPeriodicity(ReportPeriodicity.Default);
      return;
    }

    setIsLoading(true);

    try {
      const data = await executeReport(resolvedReportCode, {
        startDate,
        endDate,
        ticketServiceIds,
        ticketChannelIds,
        userIds: shouldShowUsers ? userIds : undefined,
        workspaceId,
        ticketWorkspaceIds:
          shouldShowWorkspaces && ticketWorkspaceIds?.length
            ? ticketWorkspaceIds
            : workspaceId,
        automationCode,
        automationCodeExcept,
        establishmentId,
        esignRuleId,
      });
      const normalizedResult = normalizeReportResult(data);

      setReportResult(normalizedResult);
      setColumns(normalizedResult.columns);
      setRows(createReportTableRows(normalizedResult.rows, reportId));
      setPeriodicity(normalizeReportPeriodicity(normalizedResult.periodicity));
    } catch (error) {
      console.error(error);
      setReportResult(normalizeReportResult());
      setRows([]);
      setColumns([]);
      setPeriodicity(ReportPeriodicity.Default);
    } finally {
      setIsLoading(false);
    }
  }, [
    endDate,
    automationCode,
    automationCodeExcept,
    establishmentId,
    esignRuleId,
    resolvedReportCode,
    reportId,
    startDate,
    ticketChannelIds,
    ticketWorkspaceIds,
    userIds,
    shouldShowUsers,
    shouldShowWorkspaces,
    workspaceId,
    ticketServiceIds,
  ]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return {
    columns,
    filterItems,
    generatedAt,
    generatedBy,
    generationDate,
    isLoading,
    pageTitle,
    periodicity,
    reportCode: resolvedReportCode ?? getReportCode(internalReport),
    reportResult,
    rows,
    selectedMonth,
  };
};
